/**
 * WHAT YOUR DECISION DID — and, more importantly, what it did not do.
 *
 * This replaces a green success toast. The toast's text was accurate, but a
 * toast that auto-dismisses is how "your decision was recorded and the
 * schedule did not move" becomes "done" in a reader's head. The backend
 * states the refusal in three separate fields and names every table it
 * declined to write; none of that survived a toast.
 *
 * The panel persists until dismissed, and its shape is the point:
 *
 *   RECORDED       what is now on the record, and who put it there
 *   NOT WRITTEN    every deferred write, with the blocker for each
 *
 * Deliberately absent: any per-project autonomy mode. There is no
 * autonomy-level field in this platform's schema and nothing reads one at
 * runtime, so a mode badge here would be a runtime fact that does not exist.
 */
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { AUTONOMY_COPY, presentDecisionOutcome } from "./autonomyPresentation";
import { FileCheck2, Ban, X, HelpCircle } from "lucide-react";

/**
 * Outcome tone. `recorded` is deliberately not green: the decision is real
 * and audited, and the schedule did not move. Green would collapse those.
 */
const OUTCOME_TONE = {
  recorded: {
    card: "border-indigo-500/40 bg-indigo-500/5",
    badge: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
    Icon: FileCheck2,
  },
  applied: {
    card: "border-emerald-500/40 bg-emerald-500/5",
    badge: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    Icon: FileCheck2,
  },
  unknown: {
    card: "border-amber-500/40 bg-amber-500/5",
    badge: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    Icon: HelpCircle,
  },
};

const DecisionOutcomePanel = ({ response, onDismiss }) => {
  const outcome = presentDecisionOutcome(response);
  if (!outcome) return null;
  const style = OUTCOME_TONE[outcome.tone] || OUTCOME_TONE.unknown;
  const { Icon } = style;

  return (
    <Card className={style.card} data-testid="decision-outcome">
      <CardContent className="py-3 px-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <Icon className="w-4 h-4" />
              {AUTONOMY_COPY.outcomeTitle}
            </p>
            <Badge
              variant="outline"
              className={`text-[10px] ${style.badge}`}
              data-testid="outcome-state"
            >
              {outcome.label}
            </Badge>
          </div>
          {onDismiss ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs shrink-0"
              onClick={onDismiss}
              data-testid="dismiss-outcome"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              {AUTONOMY_COPY.dismiss}
            </Button>
          ) : null}
        </div>

        <p className="text-xs leading-relaxed" data-testid="outcome-meaning">
          {outcome.meaning}
        </p>

        {/* The server's own sentence, which usually names what was untouched
            more precisely than any wording of ours would. */}
        {outcome.message ? (
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="outcome-message">
            {outcome.message}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
          {outcome.state ? <span data-testid="outcome-record-state">State: {outcome.state}</span> : null}
          {outcome.decidedBy ? <span>Decided by {outcome.decidedBy}</span> : null}
          {outcome.decidedAt ? <span>{outcome.decidedAt.slice(0, 10)}</span> : null}
          {outcome.appliedCount !== null ? (
            <span data-testid="applied-count">
              {outcome.appliedCount} activit{outcome.appliedCount === 1 ? "y" : "ies"} changed
            </span>
          ) : null}
        </div>

        {/* An approved count beside applied:false reads as a change count
            unless it says what it actually counts. */}
        {outcome.approvedActivityCount !== null || outcome.approvedPackageCount !== null ? (
          <div className="space-y-0.5" data-testid="approved-counts">
            <p className="text-[10px] text-muted-foreground">
              {outcome.approvedActivityCount !== null
                ? `${outcome.approvedActivityCount} activities`
                : ""}
              {outcome.approvedActivityCount !== null && outcome.approvedPackageCount !== null
                ? " · "
                : ""}
              {outcome.approvedPackageCount !== null
                ? `${outcome.approvedPackageCount} packages`
                : ""}
              {" covered by the analysis"}
            </p>
            {outcome.approvedCountCaveat ? (
              <p className="text-[10px] text-muted-foreground" data-testid="approved-count-caveat">
                {outcome.approvedCountCaveat}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* THE ABSTENTION LANE — what the platform declined to write, and why */}
        {outcome.deferredWrites.length ? (
          <div className="space-y-1.5 border-t border-border/60 pt-2" data-testid="deferred-writes">
            <p className="text-[11px] font-medium flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" />
              {AUTONOMY_COPY.deferredTitle}
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {outcome.deferredWritesLead}
            </p>
            {outcome.deferredWrites.map((write, index) => (
              <div key={index} className="space-y-0.5" data-testid="deferred-write">
                <p className="text-[11px] font-mono">{write.target}</p>
                {write.description ? (
                  <p className="text-[10px] text-muted-foreground">{write.description}</p>
                ) : null}
                {write.blockedBy ? (
                  <p className="text-[10px] text-muted-foreground" data-testid="deferred-blocker">
                    Blocked by: {write.blockedBy}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {outcome.silentlyUnchanged ? (
          <p className="text-[10px] text-muted-foreground" data-testid="silently-unchanged">
            {AUTONOMY_COPY.unchangedNote}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DecisionOutcomePanel;
